/* ==========================================================================
   Slot picker + direct-to-checkout booking flow.

   Progressive enhancement: the Liquid section renders a real <select name="id">
   inside .slots__fallback so the page books fine with JavaScript disabled.
   When this script runs it builds the button grid and hides the select,
   keeping it as the source of truth for the submitted variant.
   ========================================================================== */

(function () {
  'use strict';

  var LOW_SEATS = 3;

  /* ---------- date helpers ------------------------------------------------
     Slot times are *floating* local times in the store's timezone
     ("2026-08-20T18:00"). Parsing them as UTC and formatting in UTC
     round-trips the wall clock unchanged — which is what we want to display.
  ------------------------------------------------------------------------ */

  function asFloating(iso) {
    return new Date(iso.slice(0, 16) + ':00Z');
  }

  function fmt(iso, opts) {
    var o = Object.assign({ timeZone: 'UTC' }, opts);
    return new Intl.DateTimeFormat(undefined, o).format(asFloating(iso));
  }

  function dayLabel(iso) {
    var today = new Date();
    var todayKey =
      today.getFullYear() +
      '-' +
      String(today.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(today.getDate()).padStart(2, '0');
    var key = iso.slice(0, 10);
    var pretty = fmt(iso, { weekday: 'long', day: 'numeric', month: 'long' });
    if (key === todayKey) return 'Today · ' + pretty;
    return pretty;
  }

  function timeLabel(iso) {
    return fmt(iso, { hour: 'numeric', minute: '2-digit' });
  }

  /** Offset (minutes) of `tz` at the instant the floating time represents. */
  function zoneOffsetMinutes(iso, tz) {
    try {
      var probe = asFloating(iso);
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'longOffset',
      }).formatToParts(probe);
      var name = (parts.find(function (p) { return p.type === 'timeZoneName'; }) || {}).value || '';
      var m = /GMT([+-])(\d{1,2}):?(\d{2})?/.exec(name);
      if (!m) return 0;
      var sign = m[1] === '-' ? -1 : 1;
      return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3] || '0', 10));
    } catch (e) {
      return 0;
    }
  }

  /** "6:00 pm your time" — only meaningful when the viewer isn't in store tz. */
  function viewerTimeLabel(iso, storeTz) {
    var viewerTz;
    try {
      viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return null;
    }
    if (!viewerTz || viewerTz === storeTz) return null;

    var delta = zoneOffsetMinutes(iso, viewerTz) - zoneOffsetMinutes(iso, storeTz);
    if (delta === 0) return null;

    var shifted = new Date(asFloating(iso).getTime() + delta * 60000);
    var same = shifted.toISOString().slice(0, 10) === iso.slice(0, 10);
    return new Intl.DateTimeFormat(undefined, {
      timeZone: 'UTC',
      hour: 'numeric',
      minute: '2-digit',
      weekday: same ? undefined : 'short',
    }).format(shifted);
  }

  /* ---------- the picker -------------------------------------------------- */

  function initBooking(root) {
    var dataEl = root.querySelector('[data-slot-data]');
    if (!dataEl) return;

    var slots;
    try {
      slots = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var form = root.querySelector('[data-booking-form]');
    var select = root.querySelector('[data-slot-select]');
    var fallback = root.querySelector('.slots__fallback');
    var grid = root.querySelector('[data-slot-grid]');
    var chosen = root.querySelector('[data-chosen]');
    var priceEl = root.querySelector('[data-price]');
    var submit = root.querySelector('[data-submit]');
    var errorEl = root.querySelector('[data-error]');
    var qtyInput = root.querySelector('[data-qty]');
    var storeTz = root.dataset.timezone || 'UTC';
    var mode = root.dataset.bookingMode || 'group';
    var duration = root.dataset.duration || '60';

    var props = {
      session: root.querySelector('[data-prop-session]'),
      start: root.querySelector('[data-prop-start]'),
      duration: root.querySelector('[data-prop-duration]'),
      mode: root.querySelector('[data-prop-mode]'),
      tz: root.querySelector('[data-prop-tz]'),
    };

    var dated = slots.filter(function (s) { return s.iso; });
    dated.sort(function (a, b) { return a.iso.localeCompare(b.iso); });

    var current = null;

    if (props.tz) {
      try {
        props.tz.value = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      } catch (e) { /* leave blank */ }
    }
    if (props.duration) props.duration.value = duration;
    if (props.mode) props.mode.value = mode;

    /* ----- render ----- */

    if (dated.length && grid) {
      var groups = [];
      var byDay = {};
      dated.forEach(function (slot) {
        var key = slot.iso.slice(0, 10);
        if (!byDay[key]) {
          byDay[key] = [];
          groups.push(key);
        }
        byDay[key].push(slot);
      });

      var frag = document.createDocumentFragment();

      groups.forEach(function (key) {
        var day = document.createElement('div');
        day.className = 'slots__day';

        var label = document.createElement('div');
        label.className = 'slots__daylabel';
        label.textContent = dayLabel(byDay[key][0].iso);
        day.appendChild(label);

        var row = document.createElement('div');
        row.className = 'slots__row';

        byDay[key].forEach(function (slot) {
          row.appendChild(slotButton(slot));
        });

        day.appendChild(row);
        frag.appendChild(day);
      });

      grid.appendChild(frag);
      if (fallback) fallback.hidden = true;
    }

    function slotButton(slot) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'slot';
      btn.setAttribute('aria-pressed', 'false');
      btn.dataset.variantId = slot.id;

      var low = slot.available && slot.tracked && slot.inventory > 0 && slot.inventory <= LOW_SEATS;
      if (low) btn.classList.add('slot--low');

      var time = document.createElement('span');
      time.className = 'slot__time';
      time.textContent = timeLabel(slot.iso);
      btn.appendChild(time);

      var meta = document.createElement('span');
      meta.className = 'slot__seats';

      if (!slot.available) {
        btn.disabled = true;
        meta.textContent = 'Full';
      } else if (slot.tracked && slot.inventory > 0) {
        meta.textContent = slot.inventory + (slot.inventory === 1 ? ' seat left' : ' seats left');
      } else {
        var viewer = viewerTimeLabel(slot.iso, storeTz);
        meta.textContent = viewer ? viewer + ' your time' : 'Open';
      }
      btn.appendChild(meta);

      btn.addEventListener('click', function () { choose(slot); });
      return btn;
    }

    /* ----- selection ----- */

    function choose(slot) {
      current = slot;
      if (select) select.value = String(slot.id);

      Array.prototype.forEach.call(root.querySelectorAll('.slot'), function (b) {
        b.setAttribute('aria-pressed', String(b.dataset.variantId === String(slot.id)));
      });

      var pretty =
        fmt(slot.iso, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) +
        ' · ' +
        timeLabel(slot.iso);

      if (chosen) {
        var viewer = viewerTimeLabel(slot.iso, storeTz);
        chosen.innerHTML = '';
        var strong = document.createElement('strong');
        strong.textContent = pretty;
        chosen.appendChild(document.createTextNode('Selected: '));
        chosen.appendChild(strong);
        if (viewer) {
          var span = document.createElement('span');
          span.style.opacity = '.7';
          span.textContent = '  (' + viewer + ' your time)';
          chosen.appendChild(span);
        }
      }

      if (priceEl && slot.priceFormatted) priceEl.innerHTML = slot.priceFormatted;

      if (props.session) props.session.value = pretty + ' (' + storeTz + ')';
      if (props.start) props.start.value = slot.iso;

      if (qtyInput) {
        var max = slot.tracked && slot.inventory > 0 ? slot.inventory : 10;
        qtyInput.max = String(max);
        if (Number(qtyInput.value) > max) qtyInput.value = String(max);
      }

      if (submit) {
        submit.disabled = false;
        submit.textContent = submit.dataset.labelReady || 'Book this session';
      }
      hideError();

      // Keep the URL shareable / refresh-safe.
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', slot.id);
        window.history.replaceState({}, '', url);
      } catch (e) { /* non-critical */ }
    }

    /* ----- quantity ----- */

    Array.prototype.forEach.call(root.querySelectorAll('[data-qty-step]'), function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var step = Number(btn.dataset.qtyStep);
        var max = Number(qtyInput.max || 10);
        var next = Math.min(max, Math.max(1, Number(qtyInput.value || 1) + step));
        qtyInput.value = String(next);
      });
    });

    /* ----- submit ----- */

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.setAttribute('data-visible', 'true');
    }
    function hideError() {
      if (!errorEl) return;
      errorEl.removeAttribute('data-visible');
    }

    if (form) {
      form.addEventListener('submit', function (ev) {
        if (!current && select && select.value) {
          // No-JS-rendered select was used, or preselect failed — trust the select.
          var fromSelect = dated.filter(function (s) { return String(s.id) === select.value; })[0];
          if (fromSelect) choose(fromSelect);
        }
        if (!current) {
          ev.preventDefault();
          showError('Pick a date and time first.');
          return;
        }

        ev.preventDefault();
        hideError();

        var original = submit ? submit.textContent : '';
        if (submit) {
          submit.disabled = true;
          submit.textContent = (window.theme && window.theme.strings.adding) || 'Reserving…';
        }

        var payload = {
          items: [
            {
              id: Number(current.id),
              quantity: Number(qtyInput ? qtyInput.value : 1) || 1,
              properties: {
                Session: props.session ? props.session.value : '',
                _slot_start: current.iso,
                _duration_minutes: duration,
                _booking_mode: mode,
                _customer_tz: props.tz ? props.tz.value : '',
              },
            },
          ],
        };

        fetch((window.theme && window.theme.routes.cartAdd) || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        })
          .then(function (res) {
            return res.json().then(function (body) {
              if (!res.ok) throw new Error(body.description || body.message || 'Could not reserve that slot.');
              return body;
            });
          })
          .then(function () {
            window.location.href = '/checkout';
          })
          .catch(function (err) {
            if (submit) {
              submit.disabled = false;
              submit.textContent = original;
            }
            showError(err.message || 'Something went wrong. Try again.');
          });
      });
    }

    /* ----- preselect ----- */

    var wanted = new URLSearchParams(window.location.search).get('variant');
    var initial =
      (wanted && dated.filter(function (s) { return String(s.id) === wanted && s.available; })[0]) ||
      dated.filter(function (s) { return s.available; })[0];

    if (initial) {
      choose(initial);
    } else if (submit) {
      submit.disabled = true;
      submit.textContent = dated.length ? 'All sessions are full' : 'No sessions scheduled';
    }
  }

  /* ---------- boot -------------------------------------------------------- */

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-booking]'), initBooking);

    var io = 'IntersectionObserver' in window
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              e.target.classList.add('in');
              io.unobserve(e.target);
            }
          });
        }, { threshold: 0.15 })
      : null;

    Array.prototype.forEach.call(document.querySelectorAll('.reveal'), function (el) {
      if (io) io.observe(el); else el.classList.add('in');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', function (e) {
    Array.prototype.forEach.call(e.target.querySelectorAll('[data-booking]'), initBooking);
  });
})();
